import * as React from 'react';
import {cn} from '@/lib/utils';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose?: () => void;
}

const typeStyles = {
  success:
    'bg-(--constructive) text-color-(--constructive-foreground) border border-color-(--constructive)',
  error:
    'bg-(--destructive) text-destructive-foreground border border-destructive',
  warning:
    'bg-(--warning) text-color-(--warning-foreground) border border-warning',
  info: 'bg-(--muted) text-color-(--muted-foreground) border border-muted',
};

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({message, type = 'info', duration = 3000, onClose}, ref) => {
    const [isVisible, setIsVisible] = React.useState(true);
    const [animate, setAnimate] = React.useState('opacity-0 translate-y-2');

    React.useEffect(() => {
      setTimeout(() => setAnimate('opacity-100 translate-y-0'), 10);
      const timer = setTimeout(() => {
        setAnimate('opacity-0 translate-y-2');
        setTimeout(() => {
          setIsVisible(false);
          onClose?.();
        }, 200);
      }, duration);
      return () => clearTimeout(timer);
    }, [duration, onClose]);

    if (!isVisible) return null;

    return (
      <div
        ref={ref}
        className={cn(
          'fixed top-4 right-4 z-50 px-4 py-2 rounded-md border shadow transition-all duration-200 ease-in-out',
          typeStyles[type],
          animate,
        )}
        style={{minWidth: 220}}
      >
        {message}
      </div>
    );
  },
);
Toast.displayName = 'Toast';

// Toast context for managing multiple toasts
interface ToastContextType {
  showToast: (
    message: string,
    type?: 'success' | 'error' | 'info' | 'warning',
  ) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(
  undefined,
);

export const ToastProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [toasts, setToasts] = React.useState<
    Array<{
      id: number;
      message: string;
      type: 'success' | 'error' | 'info' | 'warning';
    }>
  >([]);
  const [nextId, setNextId] = React.useState(0);

  const showToast = React.useCallback(
    (
      message: string,
      type: 'success' | 'error' | 'info' | 'warning' = 'info',
    ) => {
      const id = nextId;
      setNextId((prev) => prev + 1);
      setToasts((prev) => [...prev, {id, message, type}]);
    },
    [nextId],
  );

  const removeToast = React.useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{showToast}}>
      {children}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export {Toast};
